import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { Store } from "@ngrx/store";
import {
	GenericErrorOrdonnanceCreation,
	GetBeneficiaireToolAndDroitPatientOrdonnanceCreation,
	GetUpdatedPatientOrdonnanceCreation,
	GetUpdatedPraticienOrdonnanceCreation,
	OrdonnanceCreationActionTypes,
	SetIdelAsMobilePraticienOrdonnanceCreation,
	SetMobilePraticienOrdonnanceCreation,
	SetPatientAndMutuelleOrdonnanceCreation,
	SetPraticienOrdonnanceCreation,
	UpdateBeneficiaireToolAndDroitPatientOrdonnanceCreation,
	UpdateOrdonnanceForBeneficiaireOrdonnanceEdition,
	UpdateOrdonnanceForBeneficiaireOrdonnanceEditionSuccess,
} from "./ordonnance-creation.action";
import {
	AppState,
	MobilePraticienHelperService,
	selectAllMobilePraticiens,
	selectBeneficiaireTool,
	selectCurrentDroitPatient,
	selectCurrentIdel,
	selectCurrentPatient_FromPatient,
	selectCurrentPraticienEntite,
} from "features";
import { of } from "rxjs";
import {
	BeneficiaireTool,
	EtatServiceOrdonnance,
	MobilePraticien,
	NatureSupportDroits,
} from "@jack-sparrow-libraries/emotion-models";
import { selectOrdonnanceCreation } from "@featuresAY/ordonnance-web-ay/store/ordonnance/ordonnance-creation.selector";
import { OrdonnanceEffectHelperService } from "@featuresAY/ordonnance-web-ay/utils/ordonnance-effect-helper";
import { selectAdrResponse } from "@featuresAY/lecteur/store/lecteur/lecteur.selector";

@Injectable()
export class OrdonnanceCreationEffect {
	constructor(
		private actions$: Actions,
		private store: Store<AppState>,
	) {}

	setIdelAsMobilePraticien$ = createEffect(() =>
		this.actions$.pipe(
			ofType<SetIdelAsMobilePraticienOrdonnanceCreation>(
				OrdonnanceCreationActionTypes.SetIdelAsMobilePraticienOrdonnanceCreation,
			),
			withLatestFrom(this.store.select(selectCurrentIdel), this.store.select(selectAllMobilePraticiens)),
			switchMap(([action, idel, praticiens]) => {
				let idelAsPraticien: MobilePraticien = praticiens.find(
					(praticien) => praticien.adeli === `${idel.idFact}${idel.cleIdFact}`,
				);
				if (!idelAsPraticien) {
					idelAsPraticien = MobilePraticienHelperService.mobilePraticienFromIdel(idel);
				}
				return [new SetMobilePraticienOrdonnanceCreation(idelAsPraticien)];
			}),
		),
	);

	getUpdatedPatientOrdonnanceCreation$ = createEffect(() =>
		this.actions$.pipe(
			ofType<GetUpdatedPatientOrdonnanceCreation>(
				OrdonnanceCreationActionTypes.GetUpdatedPatientOrdonnanceCreation,
			),
			withLatestFrom(this.store.select(selectCurrentPatient_FromPatient)),
			switchMap(([action, patient]) => {
				if (action.payload === patient.id) {
					return [new SetPatientAndMutuelleOrdonnanceCreation(patient)];
				}
			}),
			catchError((error) => {
				return of(new GenericErrorOrdonnanceCreation(error));
			}),
		),
	);

	getUpdatedPraticienOrdonnanceCreation$ = createEffect(() =>
		this.actions$.pipe(
			ofType<GetUpdatedPraticienOrdonnanceCreation>(
				OrdonnanceCreationActionTypes.GetUpdatedPraticienOrdonnanceCreation,
			),
			withLatestFrom(this.store.select(selectCurrentPraticienEntite)),
			switchMap(([action, praticien]) => {
				if (action.payload === praticien.id) {
					return [new SetPraticienOrdonnanceCreation(praticien)];
				}
			}),
			catchError((error) => {
				return of(new GenericErrorOrdonnanceCreation(error));
			}),
		),
	);

	getBeneficiaireToolAndDroitPatientOrdonnanceCreation$ = createEffect(() =>
		this.actions$.pipe(
			ofType<GetBeneficiaireToolAndDroitPatientOrdonnanceCreation>(
				OrdonnanceCreationActionTypes.GetBeneficiaireToolAndDroitPatientOrdonnanceCreation,
			),
			withLatestFrom(
				this.store.select(selectBeneficiaireTool),
				this.store.select(selectCurrentDroitPatient),
				this.store.select(selectOrdonnanceCreation),
				this.store.select(selectAdrResponse),
			),
			switchMap(([action, beneficiaireTool, droitpatient, ordonnance, reponseAdr]) => {
				const natureSupportDroits = action.payload.natureSupportDroits;
				return [
					new UpdateBeneficiaireToolAndDroitPatientOrdonnanceCreation({
						beneficiaireTool: { ...beneficiaireTool } as BeneficiaireTool,
						droitPatient: { ...droitpatient },
						supportDroitData: OrdonnanceEffectHelperService.getSupportDroitsData(
							natureSupportDroits === NatureSupportDroits.ADR,
							ordonnance,
							droitpatient,
							reponseAdr,
						),
					}),
				];
			}),
			catchError((error) => {
				return of(new GenericErrorOrdonnanceCreation(error));
			}),
		),
	);

	updateDroitPatientOrdonnanceCreation$ = createEffect(() =>
		this.actions$.pipe(
			ofType<UpdateOrdonnanceForBeneficiaireOrdonnanceEdition>(
				OrdonnanceCreationActionTypes.UpdateOrdonnanceForBeneficiaireOrdonnanceEdition,
			),
			withLatestFrom(this.store.select(selectOrdonnanceCreation), this.store.select(selectCurrentDroitPatient)),
			switchMap(([action, ordonnance, droitsPatientCV]) => {
				return [
					new UpdateOrdonnanceForBeneficiaireOrdonnanceEditionSuccess({
						droitPatient: { ...droitsPatientCV },
						supportDroitData: OrdonnanceEffectHelperService.getSupportDroitsData(
							ordonnance.adrEtat === EtatServiceOrdonnance.PRESENT,
							ordonnance,
							droitsPatientCV,
						),
					}),
				];
			}),
			catchError((error) => {
				return of(new GenericErrorOrdonnanceCreation(error));
			}),
		),
	);

	genericError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType<GenericErrorOrdonnanceCreation>(OrdonnanceCreationActionTypes.GenericErrorOrdonnanceCreation),
				tap((action) => {
					console.error(action);
					// TODO implement log service
				}),
			),
		{ dispatch: false },
	);
}
